import { useCallback, useEffect, useMemo, useState } from "react";
import { faBolt, faChartLine, faCirclePlus, faGauge, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocalStorage } from "usehooks-ts";

import { Colors } from "../../design/colors";
import { Gauge, IGaugeProps } from "./Gauge";
import { FaultControl, IFaultControlProps } from "./FaultControl";
import { Throttle } from "./Throttle";
import { IPlotProps, Plot } from "./Plot";
import { MessageKeySelect } from "../MessageSelect";
import { IRealtimeDataMessage, InputMode, PacketId, ControlMode } from "../../utils/common-types";
import { PlaybackButton } from "../navbar/PlaybackButton";
import { PlaybackSlider } from "../navbar/PlaybackSlider";
import { useDevices } from "../../hooks/useDevices";
import { ExportButton } from "../navbar/ExportButton";
import { SortableWidgetsContext, SortableWidgetsItem } from "./sortable";
import { Position } from "./Position";
import { hidSend } from "../../connections/hid";
import { serializeClearAllFaultsRequest } from "../../utils/common-types";

enum WidgetType {
    Plot,
    Gauge,
    Throttle,
    FaultControl,
}

export interface ICommonWidgetProps {
    close: () => void;
}

interface IWidgetProps {
    [ WidgetType.Plot ]: IPlotProps;
    [ WidgetType.Gauge ]: IGaugeProps;
    [ WidgetType.Throttle ]: undefined;
    [ WidgetType.FaultControl ]: IFaultControlProps;
}

interface IWidget<T extends WidgetType> {
    widgetType: T;
    widgetProps: IWidgetProps[ T ];
}

export type Widget = IWidget<WidgetType>;

const widgetElement = (
    _widget: Widget,
    controlMode: ControlMode|undefined,
    close: () => void,
    addKey: ( packetKey: keyof IRealtimeDataMessage ) => void,
    removeKey: ( packetKey: keyof IRealtimeDataMessage ) => void,
    clearAllFaults?: () => void,
): JSX.Element => {
    switch ( _widget.widgetType ) {
        case WidgetType.Plot: {
            const widget = _widget as IWidget<WidgetType.Plot>;
            return <Plot { ...widget.widgetProps } addKey={ addKey } removeKey={ removeKey } />;
        }
        case WidgetType.Gauge: {
            const widget = _widget as IWidget<WidgetType.Gauge>;
            return <Gauge { ...widget.widgetProps } close={ close } />;
        }
        case WidgetType.Throttle: {
            if ( controlMode === ControlMode.POSITION) {
                return <Position close={ close } />
            }
            return <Throttle close={ close } />;
        }
        case WidgetType.FaultControl: {
            const widget = _widget as IWidget<WidgetType.FaultControl>;
            const _clearAllFaults:  undefined | ( () => void ) = clearAllFaults ?? ( () => undefined );
            return <FaultControl { ...widget.widgetProps } close={ close } clearAllFaults={ _clearAllFaults } />;
        }
    }
};

const widget = <T extends WidgetType>( widgetType: T, widgetProps: IWidgetProps[ T ] ): IWidget<T> => {
    return {
        widgetType,
        widgetProps,
    };
};

const AddWidgetButton = ( props: {
    addWidget: ( widget: Widget ) => void;
    permitAddThrottle: boolean;
    permitAddFaultControl: boolean;
    close: () => void;
} ) => {
    const [ isHovering, setIsHovering ] = useState<boolean>( false );
    const [ isHoveringPlot, setIsHoveringPlot ] = useState<boolean>( false );
    const [ isHoveringGauge, setIsHoveringGauge ] = useState<boolean>( false );
    const [ isHoveringThrottle, setIsHoveringThrottle ] = useState<boolean>( false );
    const [ isHoveringFaultControl, setIsHoveringFaultControl ] = useState<boolean>( false );

    const [ gaugePopoverIsOpen, setGaugePopoverIsOpen ] = useState<boolean>( false );
    const [ plotPopoverIsOpen, setPlotPopoverIsOpen ] = useState<boolean>( false );

    const { addWidget, permitAddThrottle, permitAddFaultControl } = props;

    const _addWidget = useCallback( ( widget: Widget ) => {
        addWidget( widget );
        setIsHoveringPlot( false );
        setIsHoveringGauge( false );
        setIsHoveringThrottle( false );
        setIsHoveringFaultControl( false );
        setIsHovering( false );
    }, [ addWidget ] );

    let throttleIcon = null;
    if ( permitAddThrottle ) {
        throttleIcon = (
            <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ color: Colors.WHITE, opacity: isHoveringThrottle ? 1 : 0.5, marginRight: 4 }}>THROTTLE</span>
                <FontAwesomeIcon
                    icon={ faBolt }
                    size="4x"
                    style={{ padding: 8, opacity: isHoveringThrottle ? 1 : 0.5 }}
                    color={ Colors.WHITE }
                    onMouseOver={ () => setIsHoveringThrottle( true ) }
                    onMouseLeave={ () => setIsHoveringThrottle( false ) }
                    onClick={ () => _addWidget( widget( WidgetType.Throttle, undefined ) ) }
                />
            </div>
        );
    }

    let faultControlIcon = null;
    if ( permitAddFaultControl ) {
        faultControlIcon = (
            <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ color: Colors.WHITE, opacity: isHoveringFaultControl ? 1 : 0.5, marginRight: 4 }}>FAULT CONTROL</span>
                <FontAwesomeIcon
                    icon={ faTriangleExclamation }
                    size="4x"
                    style={{ padding: 8, opacity: isHoveringFaultControl ? 1 : 0.5 }}
                    color={ Colors.WHITE }
                    onMouseOver={ () => setIsHoveringFaultControl( true ) }
                    onMouseLeave={ () => setIsHoveringFaultControl( false ) }
                    onClick={ () => _addWidget( widget( WidgetType.FaultControl, faultControlProps() ) ) }
                />
            </div>
        );
    }

    let icons = null;
    if ( isHovering || gaugePopoverIsOpen || plotPopoverIsOpen ) {
        icons = (
            <>
                { faultControlIcon }
                { throttleIcon }
                <MessageKeySelect
                    packetId={ PacketId.REALTIME_DATA }
                    onSelect={ key => _addWidget( widget( WidgetType.Gauge, { packetKey: key } ) ) }
                    exclude={ [ "controllerState", "faultCodeDeprecated", "faultsBitmapLower32", "faultsBitmapUpper16" ] }
                    onOpening={ () => setGaugePopoverIsOpen( true ) }
                    onClose={ () => setGaugePopoverIsOpen( false ) }
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ color: Colors.WHITE, opacity: isHoveringGauge ? 1 : 0.5, marginRight: 4 }}>GAUGE</span>
                        <FontAwesomeIcon
                            icon={ faGauge }
                            size="4x"
                            style={{ padding: 8, opacity: isHoveringGauge ? 1 : 0.5 }}
                            color={ Colors.WHITE }
                            onMouseOver={ () => setIsHoveringGauge( true ) }
                            onMouseLeave={ () => setIsHoveringGauge( false ) }
                        />
                    </div>
                </MessageKeySelect>
                <MessageKeySelect
                    packetId={ PacketId.REALTIME_DATA }
                    onSelect={ key => _addWidget( widget( WidgetType.Plot, { packetKeys: [ key ] } ) ) }
                    exclude={ [ "controllerState", "faultCodeDeprecated", "faultsBitmapLower32", "faultsBitmapUpper16" ] }
                    onOpening={ () => setPlotPopoverIsOpen( true ) }
                    onClose={ () => setPlotPopoverIsOpen( false ) }
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ color: Colors.WHITE, opacity: isHoveringPlot ? 1 : 0.5, marginRight: 4 }}>CHART</span>
                        <FontAwesomeIcon
                            icon={ faChartLine }
                            size="4x"
                            style={{ padding: 8, opacity: isHoveringPlot ? 1 : 0.5 }}
                            color={ Colors.WHITE }
                            onMouseOver={ () => setIsHoveringPlot( true ) }
                            onMouseLeave={ () => setIsHoveringPlot( false ) }
                        />
                    </div>
                </MessageKeySelect>
            </>
        );
    }

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                opacity: isHovering ? 1 : 0.5,
                alignItems: "flex-end",
            }}
            onMouseOver={ () => setIsHovering( true ) }
            onMouseLeave={ () => setIsHovering( false ) }
        >
            { icons }
            <FontAwesomeIcon
                icon={ faCirclePlus }
                size="4x"
                color={ Colors.WHITE }
                style={{ padding: 8 }}
            />
        </div>
    );
};

const MIN_CARD_WIDTH = 550;

const hasWidgetOfType = ( widgets: Widget[], widgetType: WidgetType ): boolean => widgets.findIndex( w => w.widgetType === widgetType ) !== -1;

const faultControlProps = (): IFaultControlProps => {
    return { faultBitmapLowerPacketKey: "faultsBitmapLower32", faultBitmapUpperPacketKey: "faultsBitmapUpper16" };
}

const defaultWidgets: Widget[] = [
    widget( WidgetType.Throttle, undefined ),
    widget( WidgetType.Plot, { packetKeys: [ "motorSpeed", "servoPulse" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "batteryVoltage" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "uptimeMs" ] } ),
    widget( WidgetType.Plot, { packetKeys: [ "motorCurrent", "batteryCurrent" ] } ),
    widget( WidgetType.Gauge, { packetKey: "inverterTemp" } ),
    widget( WidgetType.Gauge, { packetKey: "controllerState" } ),
    widget( WidgetType.FaultControl, faultControlProps() ),
];

const PANEL_PADDING = 16;

const gaugePacketKeysToRemove: string[] = [ "faultCodeDeprecated" ];

// This is necessary to handle renamed widgets not matching with local storage.
const renameWidgets = ( ws: Widget[] ): Widget[] => {
    let modified = false;
    let newWidgets = ws.filter( ( _widget ) => {
        switch ( _widget.widgetType ) {
            case WidgetType.Gauge: {
                const widget = _widget as IWidget<WidgetType.Gauge>;
                const packetKey = widget?.widgetProps?.packetKey as string;
                if ( gaugePacketKeysToRemove.includes( packetKey ) ) {
                    modified = true;
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    });
    if ( modified ) {
        ws = newWidgets;
    }
    newWidgets = ws.map( ( _widget ) => {
        switch ( _widget.widgetType ) {
            case WidgetType.Gauge: {
                const widget = _widget as IWidget<WidgetType.Gauge>;
                if ( widget?.widgetProps?.packetKey as string === "faultCode" ) {
                    modified = true;
                    // Change a Gauge into a FaultControl
                    let widgetProps = { ...widget.widgetProps, ...faultControlProps() } as any;
                    delete widgetProps.packetKey;
                    return { widgetType: WidgetType.FaultControl, widgetProps } as Widget;
                }
                return _widget;
            }
            default:
                return _widget;
        }
    });
    if ( modified ) {
        ws = newWidgets;
    }
    return ws;
};

export const WidgetPanel = () => {
    const { connectedDevice } = useDevices();
    const [ containerWidth, setContainerWidth ] = useState<number>( 1 );
    const [ sliderHeight, setSliderHeight ] = useState<number>( 1 );
    const panelWidth = Math.max( containerWidth - PANEL_PADDING * 2, 1 );
    const enableThrottle = connectedDevice !== undefined && connectedDevice.initialized && connectedDevice.inputConfig.inputMode === InputMode.USB;
    const controlMode = enableThrottle? connectedDevice.controlMode : undefined;

    const [ widgets, setWidgets ] = useLocalStorage<Widget[]>( "dash", defaultWidgets );

    const [ widgetWidth, widgetHeight ] = useMemo( () => {
        const numColumns = Math.floor( panelWidth / Math.min( MIN_CARD_WIDTH, panelWidth ) );
        const _widgetWidth = Math.floor( panelWidth / numColumns );
        const _widgetHeight = Math.floor( _widgetWidth / 1.5 );

        return [ _widgetWidth, _widgetHeight ]
    }, [ panelWidth ] );

    // This is necessary to handle renamed widgets not matching with local storage.
    useEffect(() => {
        let newWidgets = renameWidgets( widgets );
        if ( newWidgets !== widgets ) {
            setWidgets( newWidgets );
        }
    }, [ widgets, setWidgets ]);

    const addWidget = useCallback( ( widget: Widget ) => {
        setWidgets( ws => {
            // Next two checks are to prevent adding widget more than once
            if ( widget.widgetType === WidgetType.Throttle && hasWidgetOfType( ws, WidgetType.Throttle ) ) {
                return ws;
            }

            if ( widget.widgetType === WidgetType.FaultControl && hasWidgetOfType( ws, WidgetType.FaultControl ) ) {
                return ws;
            }

            return [ ...ws, widget ];
        } );
    }, [ setWidgets ] );

    const removeWidget = useCallback( ( index: number ) => {
        setWidgets( ws => ws.slice( 0, index ).concat( ws.slice( index + 1 ) ) );
    }, [ setWidgets ] );

    const addKey = useCallback( ( index: number, key: keyof IRealtimeDataMessage ) => {
        setWidgets( ws => {
            const _widget = ws[ index ];
            if ( _widget === undefined || _widget.widgetType !== WidgetType.Plot ) {
                return ws;
            }

            const widget = _widget as IWidget<WidgetType.Plot>;

            return [ ...ws.slice( 0, index ), { ...widget, widgetProps: { ...widget.widgetProps, packetKeys: [ ...widget.widgetProps.packetKeys, key ] } } as unknown as Widget, ...ws.slice( index + 1 ) ];
        } );
    }, [ setWidgets ] );

    const removeKey = useCallback( ( index: number, key: keyof IRealtimeDataMessage ) => {
        setWidgets( ws => {
            const _widget = ws[ index ];
            if ( _widget === undefined || _widget.widgetType !== WidgetType.Plot ) {
                return ws;
            }

            const widget = _widget as IWidget<WidgetType.Plot>;

            const keyIndex = widget.widgetProps.packetKeys.indexOf( key );
            if ( keyIndex === -1 ) {
                return ws;
            }

            if ( widget.widgetProps.packetKeys.length === 1 ) {
                return ws.slice( 0, index ).concat( ws.slice( index + 1 ) );
            }

            return [
                ...ws.slice( 0, index ),
                { ...widget,
                    widgetProps: {
                        ...widget.widgetProps,
                        packetKeys: [ ...widget.widgetProps.packetKeys.slice( 0, keyIndex ), ...widget.widgetProps.packetKeys.slice( keyIndex + 1 ) ]
                    }
                } as unknown as Widget,
                ...ws.slice( index + 1 )
            ];
        } );
    }, [ setWidgets ] );

    const clearAllFaults = useCallback( () => {
        if ( connectedDevice !== undefined ) {
            hidSend( connectedDevice.handle, serializeClearAllFaultsRequest({}) );
        }
    }, [ connectedDevice ]);
    const widgetCards = useMemo( () => {
        const cards: JSX.Element[] = [];
        const marginSize = 8;
        const adjustedWidgetWidth = widgetWidth - marginSize * 2; // subtract margin on both sides

        const cardStyle: React.CSSProperties = {
            display: "flex",
            borderRadius: 10,
            backgroundColor: Colors.DARK_GREEN,
            boxShadow: "1px 2px 9px #222222",
            flexDirection: "column",
            margin: `${marginSize}px`,
        };

        for ( const [ i, widget ] of widgets.entries() ) {
            if ( widget.widgetType === WidgetType.Throttle && !enableThrottle ) {
                continue;
            }

            cards.push(
                <SortableWidgetsItem key={ i } id={ i.toString() } style={{ width: adjustedWidgetWidth, height: widgetHeight, padding: 16, ...cardStyle}}>
                    { widgetElement( widget, controlMode, removeWidget.bind( null, i ), addKey.bind( null, i ), removeKey.bind( null, i ), clearAllFaults ) }
                </SortableWidgetsItem>
            )
        }

        return cards;
    }, [ enableThrottle, widgets, widgetWidth, widgetHeight, removeWidget, addKey, removeKey, controlMode, clearAllFaults ] );

    return (
        <div ref={ e => e === null ? undefined : setContainerWidth( e.clientWidth ) } style={{ display: "flex", padding: PANEL_PADDING, flex: 1 }}>
            <SortableWidgetsContext
                widgets={ widgets }
                setWidgets={ setWidgets }
            >
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        alignContent: "flex-start",
                        position: "relative",
                        paddingBottom: sliderHeight,
                    }}
                >
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                        { widgetCards }
                    </div>
                </div>
            </SortableWidgetsContext>
            <div style={{ position: "fixed", bottom: sliderHeight - 16, right: 16 }}>
                <AddWidgetButton
                    addWidget={ addWidget }
                    permitAddThrottle={ enableThrottle && !hasWidgetOfType( widgets, WidgetType.Throttle ) }
                    permitAddFaultControl={ !hasWidgetOfType( widgets, WidgetType.FaultControl ) }
                    close={ removeWidget.bind( null, widgets.length ) } />
            </div>
            <div ref={ e => setSliderHeight( e?.clientHeight || 0 ) } style={{ position: "fixed", bottom: 0, right: 0, width: containerWidth }}>
                <div style={{ padding: 32 }}>
                    <div style={{ display: "flex", padding: 16, borderRadius: 8, backgroundColor: Colors.DARK_GREEN, boxShadow: "1px 2px 9px #222222" }}>
                        <div style={{ marginRight: 16, display: "flex", alignItems: "center" }}>
                            <ExportButton />
                            <div style={{ width: 8 }} />
                            <PlaybackButton />
                        </div>
                        <PlaybackSlider packetId={ PacketId.REALTIME_DATA } />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IWidget;
